'use client'

import Image from "next/image";
import { useEffect, useState } from 'react';
import { settings } from '../../config/settings';
import { getUserLocale, setUserLocale } from '../helpers/localManager';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ChevronDown } from 'lucide-react';
import { DEFAULT_LOCALE, PREFIXED_LOCALES } from '@/lib/seo/locales';

export default function LanguageSwitcher() {
    const [value, setValue] = useState("");
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const fetchLocale = async () => {
            const locale = await getUserLocale();
            setValue(locale || settings.translation_system.default_language || "en");
        };
        fetchLocale();
    }, []);

    const selectedOption = settings.translation_system.languages.find((item) => item.key === value);

    const handleSwitch = async (key: string) => {
        setValue(key);
        setOpen(false);
        await setUserLocale(key);
        // On a localized URL (/fr/..., /es/...) the prefix decides the language,
        // so move to the same page under the chosen language's URL.
        const { pathname, search, hash } = window.location;
        const match = pathname.match(new RegExp(`^/(${PREFIXED_LOCALES.join('|')})(/.*)?$`));
        if (match) {
            const rest = match[2] || '/';
            const target = key === DEFAULT_LOCALE ? rest : `/${key}${rest === '/' ? '' : rest}`;
            window.location.assign(`${target}${search}${hash}`);
        }
    };

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 rounded-md gap-1.5 px-2 border-border/60">
                    {selectedOption ? (
                        <Image
                            src={selectedOption.flag}
                            width={18}
                            height={18}
                            alt={selectedOption.value}
                            className="rounded-sm object-cover"
                        />
                    ) : (
                        <div className="w-[18px] h-[18px] rounded-sm bg-muted animate-pulse" />
                    )}
                    <span className="text-xs font-medium hidden sm:inline">
                        {selectedOption?.value ?? ''}
                    </span>
                    <ChevronDown className="h-3 w-3 text-muted-foreground" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" sideOffset={6} className="min-w-[140px]">
                {settings.translation_system.languages.map((lang) => (
                    <DropdownMenuItem
                        key={lang.key}
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={() => handleSwitch(lang.key)}
                    >
                        <Image
                            src={lang.flag}
                            width={18}
                            height={18}
                            alt={lang.value}
                            className="rounded-sm object-cover"
                        />
                        <span className="text-sm">{lang.value}</span>
                        {value === lang.key && (
                            <span className="ml-auto text-xs text-primary">✓</span>
                        )}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
